import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

const WHATSAPP_NUMBER = "8898798857";

// Gallery images
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_c6475175-9a65-4c2a-91e3-a6c8195373e3/artifacts/9vkfiwyz_WhatsApp%20Image%202026-01-18%20at%2012.03.00%20PM%20%281%29.jpeg";
const SALON_IMAGE_1 = "https://customer-assets.emergentagent.com/job_c6475175-9a65-4c2a-91e3-a6c8195373e3/artifacts/vqdnwizr_WhatsApp%20Image%202026-01-18%20at%2012.03.00%20PM%20%2810%29.jpeg";
const SALON_IMAGE_2 = "https://customer-assets.emergentagent.com/job_c6475175-9a65-4c2a-91e3-a6c8195373e3/artifacts/2c4is3jk_WhatsApp%20Image%202026-01-18%20at%2012.03.00%20PM%20%2812%29.jpeg";
const SALON_IMAGE_3 = "https://customer-assets.emergentagent.com/job_c6475175-9a65-4c2a-91e3-a6c8195373e3/artifacts/0jeoq4bg_WhatsApp%20Image%202026-01-18%20at%2012.03.00%20PM%20%283%29.jpeg";

const galleryImages = [
  { src: HERO_IMAGE, category: "Styling", title: "Curly Hair Styling" },
  { src: SALON_IMAGE_1, category: "Styling", title: "Curly Hair Transformation" },
  { src: SALON_IMAGE_2, category: "Colour", title: "Balayage Treatment" },
  { src: SALON_IMAGE_3, category: "Styling", title: "Volume Styling" },
  { src: HERO_IMAGE, category: "Styling", title: "Natural Curls" },
  { src: SALON_IMAGE_1, category: "Colour", title: "Hair Colour" },
  { src: SALON_IMAGE_2, category: "Treatments", title: "Hair Treatment" },
  { src: SALON_IMAGE_3, category: "Styling", title: "Professional Styling" },
];

const categories = ["All", "Styling", "Colour", "Treatments"];

export default function GalleryPage() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [lightboxImage, setLightboxImage] = useState(null);

  const filteredImages = selectedCategory === "All" 
    ? galleryImages 
    : galleryImages.filter(img => img.category === selectedCategory);

  const handleBookAppointment = () => {
    const message = encodeURIComponent("Hi, I'd like to book an appointment at Bunty Hair Master.");
    window.open(`https://wa.me/91${WHATSAPP_NUMBER}?text=${message}`, "_blank");
  };

  return (
    <div className="min-h-screen pt-20" data-testid="gallery-page">
      {/* Hero Section */}
      <section className="section-spacing bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl">
            <p className="font-['Cormorant_Garamond'] text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">
              Our Work
            </p>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-white font-semibold leading-tight mb-6">
              Gallery
            </h1>
            <p className="text-white/70 text-lg">
              Browse through our portfolio of transformations. Every client leaves our salon 
              feeling confident and beautiful.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="section-spacing bg-[#121212]">
        <div className="max-w-7xl mx-auto container-padding">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-4 mb-12" data-testid="gallery-filters">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? "bg-[#D4AF37] text-black"
                    : "bg-transparent border border-white/20 text-white hover:border-[#D4AF37] hover:text-[#D4AF37]"
                }`}
                data-testid={`filter-${category.toLowerCase()}`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          <div className="gallery-grid" data-testid="gallery-grid">
            {filteredImages.map((image, index) => (
              <div 
                key={index}
                className="gallery-item"
                onClick={() => setLightboxImage(image)}
                data-testid={`gallery-item-${index}`}
              >
                <img src={image.src} alt={image.title} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 hover:opacity-100 transition-opacity flex items-end p-4">
                  <div>
                    <p className="text-[#D4AF37] text-xs uppercase tracking-wider">{image.category}</p>
                    <p className="text-white font-medium">{image.title}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto container-padding text-center">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white font-semibold mb-6">
            Ready for Your Transformation?
          </h2>
          <p className="text-white/60 mb-8 max-w-2xl mx-auto">
            Let us help you achieve the look you've always wanted. Book your appointment today.
          </p>
          <Button
            onClick={handleBookAppointment}
            className="bg-[#D4AF37] text-black hover:bg-[#B5952F] font-medium px-10 py-4 rounded-none text-lg shadow-[0_0_20px_rgba(212,175,55,0.3)] hover:shadow-[0_0_30px_rgba(212,175,55,0.5)]"
            data-testid="gallery-cta-book-btn"
          >
            Book Your Appointment
          </Button>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
          data-testid="lightbox"
        >
          <button 
            className="absolute top-6 right-6 text-white hover:text-[#D4AF37] transition-colors"
            onClick={() => setLightboxImage(null)}
            data-testid="lightbox-close"
          >
            <X className="w-8 h-8" />
          </button>
          <img 
            src={lightboxImage.src} 
            alt={lightboxImage.title}
            className="max-w-full max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center">
            <p className="text-[#D4AF37] text-sm uppercase tracking-wider">{lightboxImage.category}</p>
            <p className="text-white text-lg font-medium">{lightboxImage.title}</p>
          </div>
        </div>
      )}
    </div>
  );
}
